import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { BudgetLineBody } from '../types'

export function useBudgetCategories() {
  return useQuery({
    queryKey: ['budget-categories'],
    queryFn: ApiClient.budgetCategories,
    staleTime: 60 * 60_000     // 1hr; admin-managed catalogue
  })
}

export function useEventBudget(eventId: number) {
  return useQuery({
    queryKey: ['budget', eventId],
    queryFn: () => ApiClient.eventBudget(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    retry: (count, err) => {
      // Don't retry on 403 (no financial access).
      if (err instanceof Error && err.message.startsWith('403')) return false
      return count < 2
    }
  })
}

export function useCreateBudgetLine(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BudgetLineBody) => ApiClient.createBudgetLine(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budget', eventId] })
  })
}

export function useUpdateBudgetLine(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lineId, body }: { lineId: number; body: BudgetLineBody }) =>
      ApiClient.updateBudgetLine(eventId, lineId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budget', eventId] })
  })
}

export function useDeleteBudgetLine(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (lineId: number) =>
      ApiClient.deleteBudgetLine(eventId, lineId).then(() => lineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budget', eventId] })
  })
}

export function useBaselineBudget(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ApiClient.baselineBudget(eventId),
    onSuccess: (payload) => qc.setQueryData(['budget', eventId], payload)
  })
}

export function useRebaselineBudget(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ApiClient.rebaselineBudget(eventId),
    onSuccess: (payload) => qc.setQueryData(['budget', eventId], payload)
  })
}

// MERAC actuals rollup — read-only, no in-app mutations touch it (data
// is populated by the ontrack .ps1 → meracSync → normaliser pipe). 30s
// staleTime keeps it fresh during a session without hammering on every
// component remount; react-query's window-focus refetch handles the
// "I just left this tab open all day" case. 403 (non-financial user)
// is suppressed so the BudgetTab's existing financial hard-stop is the
// single source of the locked-state message.
export function useEventMeracRollup(eventId: number) {
  return useQuery({
    queryKey: ['event', eventId, 'merac-rollup'],
    queryFn: () => ApiClient.eventMeracRollup(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 30_000,
    retry: (count, err) => {
      if (err instanceof Error && err.message.startsWith('403')) return false
      return count < 2
    }
  })
}
